import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../lib/supabase';
import {
    Plus, FileText, CheckCircle, Clock, ExternalLink,
    Edit3, Loader2, TrendingUp, Trash2, AlertCircle,
    X, Search, Filter, ArrowUpRight
} from 'lucide-react';
import toast, { Toaster } from 'react-hot-toast';

const InvoiceDashboard = () => {
    // --- State Management ---
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(true);

    // Modals
    const [isFormModalOpen, setIsFormModalOpen] = useState(false);
    const [isSettleModalOpen, setIsSettleModalOpen] = useState(false);

    // Selection/Editing
    const [editId, setEditId] = useState(null);
    const [selectedInvoice, setSelectedInvoice] = useState(null);
    const [settleAmount, setSettleAmount] = useState('');

    // Form Data
    const [formData, setFormData] = useState({
        invoice_number: '',
        base_amount: '',
        settled_amount: '',
        date_time: '',
        pdf: null,
        pdf_url: '',
        status: 'pending'
    });

    // --- Data Fetching ---
    const fetchInvoices = useCallback(async () => {
        setFetching(true);
        try {
            const { data, error } = await supabase
                .from('invoices')
                .select('*')
                .order('created_at', { ascending: false });

            if (error) throw error;
            setInvoices(data || []);
        } catch (err) {
            toast.error("Failed to load invoices");
        } finally {
            setFetching(false);
        }
    }, []);

    useEffect(() => {
        fetchInvoices();
    }, [fetchInvoices]);

    // --- Modal Handlers ---
    const openFormModal = (invoice = null) => {
        if (invoice) {
            setEditId(invoice.id);
            setFormData({
                invoice_number: invoice.invoice_number,
                base_amount: invoice.base_amount || '',
                settled_amount: invoice.settled_amount || '',
                date_time: new Date(invoice.date_time).toISOString().slice(0, 16),
                pdf_url: invoice.pdf_url,
                pdf: null,
                status: invoice.status
            });
        } else {
            setEditId(null);
            const now = new Date();
            now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
            setFormData({
                invoice_number: '',
                base_amount: '',
                settled_amount: '',
                date_time: now.toISOString().slice(0, 16),
                pdf: null,
                pdf_url: '',
                status: 'pending'
            });
        }
        setIsFormModalOpen(true);
    };

    const openSettleModal = (invoice) => {
        setSelectedInvoice(invoice);
        setSettleAmount(invoice.base_amount); // Pre-fill with original amount
        setIsSettleModalOpen(true);
    };

    // --- CRUD Operations ---
    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure you want to delete this invoice?")) return;

        try {
            const { error } = await supabase.from('invoices').delete().eq('id', id);
            if (error) throw error;
            toast.success("Invoice deleted");
            fetchInvoices();
        } catch (err) {
            toast.error(err.message);
        }
    };

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            let publicUrl = formData.pdf_url;

            // Handle File Upload
            if (formData.pdf) {
                const filePath = `invoices/${Date.now()}_${formData.pdf.name}`;
                const { error: uploadError } = await supabase.storage.from('documents').upload(filePath, formData.pdf);
                if (uploadError) throw uploadError;

                const { data: urlData } = supabase.storage.from('documents').getPublicUrl(filePath);
                publicUrl = urlData.publicUrl;
            }

            const payload = {
                invoice_number: formData.invoice_number,
                base_amount: parseFloat(formData.base_amount || 0),
                settled_amount: parseFloat(formData.settled_amount || 0),
                date_time: formData.date_time,
                pdf_url: publicUrl,
                status: formData.status
            };

            const { error } = editId
                ? await supabase.from('invoices').update(payload).eq('id', editId)
                : await supabase.from('invoices').insert([payload]);

            if (error) throw error;

            toast.success(editId ? "Invoice updated" : "Invoice created");
            setIsFormModalOpen(false);
            fetchInvoices();
        } catch (err) {
            toast.error(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleFinalSettlement = async () => {
        if (!settleAmount || isNaN(settleAmount)) return toast.error("Enter a valid amount");

        setLoading(true);
        try {
            const finalAmount = parseFloat(settleAmount);

            // 1. Update Invoice status and confirmed amount
            const { error: invError } = await supabase
                .from('invoices')
                .update({
                    status: 'done',
                    settled_amount: finalAmount
                })
                .eq('id', selectedInvoice.id);

            if (invError) throw invError;

            // 2. Fetch current total_cash from settings (ID 1)
            const { data: settings, error: setFetchError } = await supabase
                .from('settings')
                .select('total_cash')
                .eq('id', 1)
                .single();

            if (setFetchError) throw setFetchError;

            // 3. Calculate new total and update the database
            const newTotalCash = (Number(settings?.total_cash) || 0) + finalAmount;
            const { error: setUpdateError } = await supabase
                .from('settings')
                .update({
                    total_cash: newTotalCash,
                    updated_at: new Date()
                })
                .eq('id', 1);

            if (setUpdateError) throw setUpdateError;

            toast.success(`Settled! ₹${finalAmount} added to Total Cash.`);
            setIsSettleModalOpen(false);
            fetchInvoices(); // Refresh list to update KPI calculations
        } catch (err) {
            toast.error(err.message);
            console.error("Settlement Error:", err);
        } finally {
            setLoading(false);
        }
    };
    // --- Calculations ---
    // Only sum invoices where status is 'done'
    const totalRevenue = invoices
        .filter(inv => inv.status === 'done')
        .reduce((sum, inv) => sum + Number(inv.settled_amount), 0);


    return (
        <div className="p-4 md:p-8 bg-[#F8FAFC] min-h-screen text-slate-900 font-sans">
            <Toaster position="top-right" />

            <div className="max-w-6xl mx-auto">
                {/* Header Section */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-10">
                    <div>
                        <h1 className="text-3xl font-black tracking-tight text-slate-800">Invoice Manager</h1>
                        <p className="text-slate-500 font-medium">Manage construction billings and revenue tracking</p>
                    </div>
                    <button
                        onClick={() => openFormModal()}
                        className="flex items-center justify-center gap-2 bg-blue-600 text-white px-6 py-3.5 rounded-2xl font-bold shadow-xl shadow-blue-200 hover:bg-blue-700 transition-all active:scale-95"
                    >
                        <Plus size={20} strokeWidth={3} /> New Invoice
                    </button>
                </div>

                {/* KPI Section */}
                {/* KPI Section */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-6 mb-10">
                    {/* Received Cash KPI */}
                    <div className="bg-white p-6 rounded-[2rem] shadow-sm border border-slate-200 flex items-center gap-5">
                        <div className="w-14 h-14 bg-emerald-50 text-emerald-600 rounded-2xl flex items-center justify-center">
                            <CheckCircle size={28} />
                        </div>
                        <div>
                            <p className="text-slate-400 text-[10px] font-black uppercase tracking-[0.2em] mb-1">Received Revenue</p>
                            <h2 className="text-2xl font-black text-slate-800">₹{totalRevenue.toLocaleString()}</h2>
                        </div>
                    </div>

                    {/* Pending/Outstanding KPI */}
                    <div className="bg-white p-6 rounded-[2rem] shadow-sm border border-slate-200 flex items-center gap-5">
                        <div className="w-14 h-14 bg-amber-50 text-amber-600 rounded-2xl flex items-center justify-center">
                            <Clock size={28} />
                        </div>
                        <div>
                            <p className="text-slate-400 text-[10px] font-black uppercase tracking-[0.2em] mb-1">Pending Amount</p>
                            <h2 className="text-2xl font-black text-slate-800">
                                ₹{invoices
                                    .filter(inv => inv.status === 'pending')
                                    .reduce((sum, inv) => sum + Number(inv.base_amount), 0)
                                    .toLocaleString()}
                            </h2>
                        </div>
                    </div>
                </div>

                {/* Table Section */}
                <div className="bg-white rounded-[2rem] shadow-sm border border-slate-200 overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-left">
                            <thead>
                                <tr className="bg-slate-50/50 text-slate-400 text-[10px] uppercase font-black tracking-[0.15em] border-b">
                                    <th className="px-8 py-5">Invoice</th>
                                    <th className="px-8 py-5">Status</th>
                                    <th className="px-8 py-5 text-right"> Base Amount</th>
                                    <th className="px-8 py-5 text-right">Setteled Amount</th>
                                    <th className="px-8 py-5 text-center">Document</th>
                                    <th className="px-8 py-5 text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-50">
                                {fetching ? (
                                    <tr>
                                        <td colSpan="5" className="px-8 py-20 text-center">
                                            <div className="flex flex-col items-center gap-3">
                                                <Loader2 className="animate-spin text-blue-600" size={32} />
                                                <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">Fetching Data</p>
                                            </div>
                                        </td>
                                    </tr>
                                ) : invoices.length === 0 ? (
                                    <tr>
                                        <td colSpan="5" className="px-8 py-20 text-center text-slate-400 font-bold">No records found.</td>
                                    </tr>
                                ) : invoices.map((inv) => (
                                    <tr key={inv.id} className="hover:bg-slate-50/30 transition-colors group">
                                        <td className="px-8 py-6">
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 bg-slate-100 rounded-xl flex items-center justify-center text-slate-400 group-hover:bg-blue-50 group-hover:text-blue-500 transition-colors">
                                                    <FileText size={20} />
                                                </div>
                                                <div>
                                                    <p className="font-black text-slate-700">#{inv.invoice_number}</p>
                                                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-tighter">{new Date(inv.date_time).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-8 py-6">
                                            <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-black uppercase tracking-wider ${inv.status === 'done' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
                                                }`}>
                                                {inv.status === 'done' ? <CheckCircle size={14} /> : <Clock size={14} />}
                                                {inv.status === 'done' ? 'Settled' : 'Pending'}
                                            </span>
                                        </td>
                                        <td className="px-8 py-6 text-right">
                                            <p className="text-lg font-black text-slate-800">₹{inv.base_amount.toLocaleString()}</p>
                                        </td>
                                        <td className="px-8 py-6 text-right">
                                            <p className="text-lg font-black text-slate-800">₹{inv.settled_amount.toLocaleString()}</p>
                                        </td>
                                        <td className="px-8 py-6 text-center">
                                            <a href={inv.pdf_url} target="_blank" rel="noreferrer"
                                                className="inline-flex items-center gap-1.5 text-blue-600 font-black text-[10px] uppercase hover:bg-blue-50 px-3 py-2 rounded-xl transition-colors">
                                                View <ExternalLink size={12} />
                                            </a>
                                        </td>
                                        <td className="px-8 py-6 text-right">
                                            <div className="flex justify-end items-center gap-1">
                                                {inv.status === 'pending' && (
                                                    <button
                                                        onClick={() => openSettleModal(inv)}
                                                        className="mr-2 bg-slate-900 text-white px-4 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest hover:bg-slate-800 transition-all active:scale-95"
                                                    >
                                                        Settle
                                                    </button>
                                                )}
                                                <button onClick={() => openFormModal(inv)} className="p-2 text-slate-300 hover:text-blue-600 transition-colors">
                                                    <Edit3 size={18} />
                                                </button>
                                                <button onClick={() => handleDelete(inv.id)} className="p-2 text-slate-300 hover:text-red-500 transition-colors">
                                                    <Trash2 size={18} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* --- MODAL: Settlement (Adjust Amount & Confirm) --- */}
            {isSettleModalOpen && (
                <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 z-[100]">
                    <div className="bg-white rounded-[2.5rem] p-8 max-w-sm w-full shadow-2xl animate-in zoom-in duration-200">
                        <div className="flex items-center gap-4 text-amber-600 mb-6">
                            <div className="w-12 h-12 bg-amber-50 rounded-2xl flex items-center justify-center">
                                <AlertCircle size={28} />
                            </div>
                            <h2 className="text-xl font-black tracking-tight">Confirm Settlement</h2>
                        </div>

                        <p className="text-sm text-slate-500 font-medium mb-8 leading-relaxed">
                            Confirming payment for <span className="font-bold text-slate-800">#{selectedInvoice?.invoice_number}</span>. This will add the amount below to your system cash.
                        </p>

                        <div className="space-y-6">
                            <div>
                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 block">Final Received Amount (₹)</label>
                                <input
                                    type="number"
                                    className="w-full border-2 border-slate-100 p-5 rounded-2xl font-black text-2xl focus:border-blue-500 outline-none transition-all bg-slate-50 text-slate-800"
                                    value={settleAmount}
                                    onChange={(e) => setSettleAmount(e.target.value)}
                                    autoFocus
                                />
                            </div>

                            <div className="flex gap-3">
                                <button
                                    onClick={() => setIsSettleModalOpen(false)}
                                    className="flex-1 bg-slate-100 text-slate-500 py-4 rounded-2xl font-bold hover:bg-slate-200 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleFinalSettlement}
                                    disabled={loading}
                                    className="flex-2 bg-emerald-600 text-white py-4 px-6 rounded-2xl font-black uppercase text-xs tracking-widest shadow-lg shadow-emerald-100 hover:bg-emerald-700 disabled:opacity-50"
                                >
                                    {loading ? 'Updating...' : 'Add to Cash'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* --- MODAL: New / Edit Invoice --- */}
            {isFormModalOpen && (
                <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 z-[100]">
                    <div className="bg-white rounded-[2.5rem] p-10 max-w-lg w-full shadow-2xl relative overflow-hidden">
                        <button onClick={() => setIsFormModalOpen(false)} className="absolute top-8 right-8 text-slate-300 hover:text-slate-600">
                            <X size={24} />
                        </button>

                        <h2 className="text-2xl font-black text-slate-800 mb-8 flex items-center gap-3">
                            <div className="w-10 h-10 bg-blue-600 text-white rounded-xl flex items-center justify-center">
                                {editId ? <Edit3 size={20} /> : <Plus size={22} strokeWidth={3} />}
                            </div>
                            {editId ? 'Edit Invoice' : 'New Invoice Entry'}
                        </h2>

                        <form onSubmit={handleFormSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="md:col-span-2">
                                <label className="text-[10px] uppercase font-black text-slate-400 tracking-widest mb-2 block">Invoice Number</label>
                                <input
                                    type="text"
                                    className="w-full border-slate-100 border-2 p-4 rounded-2xl focus:ring-4 focus:ring-blue-50 focus:border-blue-500 focus:outline-none bg-slate-50 font-bold transition-all text-slate-700"
                                    placeholder="INV-001"
                                    value={formData.invoice_number}
                                    onChange={(e) => setFormData({ ...formData, invoice_number: e.target.value })}
                                    required
                                />
                            </div>
                            <div>
                                <label className="text-[10px] uppercase font-black text-slate-400 tracking-widest mb-2 block"> Base Amount (₹)</label>
                                <input
                                    type="number"
                                    className="w-full border-slate-100 border-2 p-4 rounded-2xl focus:ring-4 focus:ring-blue-50 focus:border-blue-500 focus:outline-none bg-slate-50 font-bold transition-all text-slate-700"
                                    placeholder="0.00"
                                    value={formData.base_amount}
                                    onChange={(e) => setFormData({ ...formData, base_amount: e.target.value })}
                                    required
                                />
                            </div>
                            <div>
                                <label className="text-[10px] uppercase font-black text-slate-400 tracking-widest mb-2 block">Date & Time</label>
                                <input
                                    type="datetime-local"
                                    className="w-full border-slate-100 border-2 p-4 rounded-2xl focus:ring-4 focus:ring-blue-50 focus:border-blue-500 focus:outline-none bg-slate-50 font-bold transition-all text-slate-700"
                                    value={formData.date_time}
                                    onChange={(e) => setFormData({ ...formData, date_time: e.target.value })}
                                    required
                                />
                            </div>
                            <div className="md:col-span-2">
                                <label className="text-[10px] uppercase font-black text-slate-400 tracking-widest mb-2 block">Attachment (PDF)</label>
                                <div className="border-2 border-dashed border-slate-200 rounded-2xl p-4 bg-slate-50">
                                    <input
                                        type="file"
                                        accept=".pdf"
                                        className="w-full text-xs text-slate-400 file:mr-4 file:py-2 file:px-6 file:rounded-xl file:border-0 file:text-[10px] file:font-black file:uppercase file:bg-blue-600 file:text-white hover:file:bg-blue-700 file:transition-all cursor-pointer"
                                        onChange={(e) => setFormData({ ...formData, pdf: e.target.files[0] })}
                                        required={!editId}
                                    />
                                </div>
                            </div>

                            <div className="md:col-span-2 flex gap-4 pt-4">
                                <button
                                    type="button"
                                    onClick={() => setIsFormModalOpen(false)}
                                    className="flex-1 bg-slate-100 text-slate-500 py-4 rounded-2xl font-bold hover:bg-slate-200 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="flex-[2] bg-blue-600 text-white py-4 rounded-2xl font-black uppercase text-xs tracking-[0.2em] hover:bg-blue-700 shadow-xl shadow-blue-100 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                                    disabled={loading}
                                >
                                    {loading && <Loader2 size={16} className="animate-spin" />}
                                    {editId ? 'Update Record' : 'Create Invoice'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default InvoiceDashboard;